<template>
    <div class="theme-editor flex h-full min-h-0 flex-col text-slate-100" @click="closeColorMenu">
        <header class="flex shrink-0 items-center gap-2 border-b border-slate-700 bg-slate-950 px-4 py-2">
            <div class="mr-2 min-w-0">
                <div class="text-base font-semibold">Theme Editor</div>
                <div class="truncate text-[10px] text-slate-400">openHASP LVGL 7 JSONL · {{ fileName || 'new theme' }}</div>
            </div>
            <span v-if="dirty" class="rounded bg-amber-700/70 px-2 py-1 text-[10px]">unsaved</span>
            <span v-if="validationMessage" class="rounded bg-red-800/80 px-2 py-1 text-[10px]">invalid</span>
            <div class="ml-auto flex gap-1">
                <button class="theme-button" @click="saveTheme">Save</button>
            </div>
        </header>

        <div class="flex min-h-0 flex-1">
            <nav class="w-44 shrink-0 space-y-1 border-r border-slate-800 bg-slate-900 p-2">
                <button v-for="tab in tabs" :key="tab.id" class="theme-nav-button" :class="{ active: activeTab === tab.id }" @click="activeTab = tab.id">
                    {{ tab.label }}
                    <span v-if="tab.count !== undefined" class="float-right text-slate-400">{{ tab.count }}</span>
                </button>
                <div class="mt-5 border-t border-slate-700 pt-3 text-[10px] leading-4 text-slate-500">
                    <div>Lossless model</div>
                    <div>Unknown records and keys are retained.</div>
                    <div>Export is compact JSONL.</div>
                </div>
            </nav>

            <main class="min-w-0 flex-1 overflow-y-auto bg-slate-800 p-4">
                <section v-if="activeTab === 'metadata'" class="theme-section">
                    <div class="theme-section-heading">
                        <div><h2>Theme metadata</h2><p>Compatibility and runtime settings written to the <code>r:"m"</code> record.</p></div>
                        <span class="theme-chip">format v{{ metadata?.v }}</span>
                    </div>
                    <div v-if="metadata" class="grid max-w-4xl grid-cols-1 gap-3 md:grid-cols-2">
                        <label class="theme-field"><span>Theme name</span><input v-model="metadata.n" @input="markDirty" /></label>
                        <label class="theme-field"><span>Runtime ID <small>(16–254)</small></span><input v-model.number="metadata.id" type="number" min="16" max="254" @input="markDirty" /></label>
                        <label class="theme-field"><span>LVGL major version</span><input v-model.number="metadata.lv" type="number" @input="markDirty" /></label>
                        <label class="theme-field"><span>Color depth</span><input v-model.number="metadata.cd" type="number" min="1" @input="markDirty" /></label>
                        <label class="theme-field"><span>Reference DPI</span><input v-model.number="metadata.dpi" type="number" min="1" @input="markDirty" /></label>
                        <label class="theme-field"><span>Reference width</span><input v-model.number="metadata.w" type="number" min="1" @input="markDirty" /></label>
                        <label class="theme-field"><span>Theme flags</span><input v-model.number="metadata.fl" type="number" min="0" @input="markDirty" /></label>
                        <label class="theme-field theme-check-field"><span>Default theme</span><input v-model="metadata.df" type="checkbox" :true-value="1" :false-value="0" @change="markDirty" /></label>
                    </div>
                </section>

                <section v-else-if="activeTab === 'colors'" class="theme-section">
                    <div class="theme-section-heading"><div><h2>Colors</h2><p>RGB888 is a convenient editing view; export always stores the exact RGB565 integer.</p></div><span class="theme-chip">{{ colors.length }} records</span></div>
                    <div class="theme-editor-toolbar theme-color-add">
                        <label>Name <input v-model.trim="newColorName" class="theme-text-input" placeholder="e.g. button_primary" /></label>
                        <ColorPicker :pureColor="newColorHex" useType="pure" format="hex" lang="En" shape="circle" @pureColorChange="newColorHex = $event" />
                        <label>RGB888 <input v-model="newColorHex" class="theme-text-input theme-hex-input" pattern="#[0-9a-fA-F]{6}" /></label>
                        <button class="theme-add-button" :disabled="!newColorName || colors.length >= maxThemeColors" @click="addColor">+ Add color</button>
                        <span v-if="colors.length >= maxThemeColors" class="theme-limit-warning">Maximum of {{ maxThemeColors }} colors reached.</span>
                    </div>
                    <div class="theme-table">
                        <div class="theme-table-row theme-table-header"><span>Picker</span><span>Identifier</span><span>RGB888</span><span>RGB565</span></div>
                        <div v-for="color in colors" :key="color.n + '-' + color._line" class="theme-table-row">
                            <ColorPicker :pureColor="colorHex(color)" useType="pure" format="hex" lang="En" shape="circle" @pureColorChange="setColorHex(color, $event)" />
                            <input :value="color.n" @change="renameColor(color, $event.target.value)" />
                            <input :value="colorHex(color)" pattern="#[0-9a-fA-F]{6}" @change="setColorHex(color, $event.target.value)" />
                            <input :value="color.v" type="number" min="0" max="65535" @change="setColor565(color, $event.target.value)" />
                        </div>
                    </div>
                </section>

                <section v-else-if="activeTab === 'styles'" class="theme-section">
                    <div class="theme-section-heading"><div><h2>Reusable styles</h2><p>Een style is een herbruikbare property-bag. De firmware gebruikt hiervoor vaste openHASP-style-namen; kies een herkenbare omschrijving en pas de properties hieronder aan.</p></div><span class="theme-chip">{{ styles.length }} styles</span></div>
                    <div class="theme-editor-toolbar">
                        <input v-model.trim="styleCatalogSearch" class="theme-style-search" placeholder="Search firmware styles…" aria-label="Search firmware styles" />
                        <select v-model="newStyleName" class="theme-add-select"><option value="">Choose a firmware style…</option><option v-for="styleName in filteredAvailableStyleNames" :key="styleName" :value="styleName">{{ styleOptionLabel(styleName) }}</option></select>
                        <button class="theme-add-button" :disabled="!newStyleName" @click="addStyle">+ Add style</button>
                    </div>
                    <input v-model="styleSearch" class="theme-search" placeholder="Filter style or property…" />
                    <div class="space-y-2">
                        <details v-for="style in filteredStyles" :key="style.declaration.n" class="theme-card theme-style-card" :open="isDetailsOpen(`style:${style.declaration.n}`)" @toggle="setDetailsOpen(`style:${style.declaration.n}`, $event)">
                            <summary class="theme-style-summary"><span class="theme-style-name">{{ styleLabel(style.declaration.n) }}</span><code>{{ style.declaration.n }}</code><span class="theme-style-description">{{ styleDescription(style.declaration.n) }}</span><span class="text-[10px] font-normal text-slate-400">{{ style.properties.length }} properties</span><button class="theme-delete-button theme-delete-small" title="Delete style and its properties" @click.prevent="removeStyle(style.declaration.n)">Delete style</button></summary>
                            <div class="space-y-3 border-t border-slate-700 p-3">
                                <div v-for="group in stateGroups(style)" :key="group.state" class="theme-state-group">
                                    <div class="mb-1 text-[11px] font-semibold text-cyan-300">{{ stateLabel(group.state) }} <code class="ml-1 text-[10px] font-normal">({{ group.state }})</code></div>
                                    <div v-for="property in group.properties" :key="property._line" class="theme-property-row">
                                        <div class="min-w-0"><span class="theme-property-label">{{ propertyLabel(property.k) }}</span><code class="ml-2">{{ property.k }}</code><span v-if="propertyRange(property.k)" class="ml-2 text-[10px] text-slate-500">{{ propertyRange(property.k) }}</span></div>
                                        <div class="flex min-w-0 items-center gap-1">
                                            <div v-if="property.k === 'r'" class="theme-radius-editor">
                                                <label class="theme-radius-toggle"><input type="checkbox" :checked="radiusIsFullyRounded(property)" @change="toggleRadius(property, $event.target.checked)" /> Fully rounded</label>
                                                <input :value="propertyValueText(property.v)" type="number" min="0" :disabled="radiusIsFullyRounded(property)" @input="setPropertyValue(property, $event.target.value, true)" />
                                            </div>
                                            <div v-else-if="propertyEditorKind(property) === 'color'" class="theme-color-menu-wrap" @click.stop>
                                                <button type="button" class="theme-color-select" @click="toggleColorMenu(property._line)"><span class="theme-color-bullet" :style="{ backgroundColor: propertyColorHex(property) }"></span><span>{{ selectedColorLabel(property) }}</span><span class="theme-color-chevron">▾</span></button>
                                                <div v-if="openColorMenu === property._line" class="theme-color-menu">
                                                    <button v-for="color in colors" :key="color.n" type="button" class="theme-color-option" @click="selectPropertyColor(property, color.n)"><span class="theme-color-bullet" :style="{ backgroundColor: colorHex(color) }"></span><span>{{ color.n }}</span><code>{{ colorHex(color) }}</code></button>
                                                </div>
                                            </div>
                                            <select v-else-if="propertyEditorKind(property) === 'enum'" :value="property.v" @change="setPropertyValue(property, $event.target.value)"><option v-for="value in propertyDefinition(property.k).values" :key="value" :value="value">{{ value }}</option></select>
                                            <input v-else-if="propertyEditorKind(property) === 'bool'" type="checkbox" :checked="Boolean(property.v)" @change="setPropertyValue(property, $event.target.checked)" />
                                            <input v-else :value="propertyValueText(property.v)" :type="propertyEditorKind(property) === 'number' ? 'number' : 'text'" :min="propertyDefinition(property.k).min" :max="propertyDefinition(property.k).max" :readonly="!isEditableProperty(property)" @input="setPropertyValue(property, $event.target.value, propertyEditorKind(property) === 'number')" />
                                            <button class="theme-property-delete" title="Remove property" @click="removeProperty(property)">×</button>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="!style.properties.length" class="theme-empty-state">No properties yet. Add the first property below.</div>
                                <div class="theme-add-property">
                                    <select v-model="propertyDrafts[style.declaration.n].key"><option value="">Add property…</option><option v-for="key in availablePropertyKeys(style)" :key="key" :value="key">{{ propertyLabel(key) }} ({{ key }})</option></select>
                                    <select v-model="propertyDrafts[style.declaration.n].state"><option v-for="state in states" :key="state" :value="state">{{ stateLabel(state) }}</option></select>
                                    <button class="theme-add-button" :disabled="!propertyDrafts[style.declaration.n].key" @click="addProperty(style.declaration.n)">+ Add property</button>
                                </div>
                            </div>
                        </details>
                    </div>
                </section>

                <section v-else-if="activeTab === 'applications'" class="theme-section">
                    <div class="theme-section-heading"><div><h2>Widget / part assignments</h2><p>Een assignment koppelt herbruikbare styles aan één vaste widget-part-combinatie. States horen bij de properties in de Styles-tab en worden door de firmware automatisch toegepast.</p></div><span class="theme-chip">{{ applications.length }} assignments</span></div>
                    <div class="theme-editor-toolbar theme-application-add">
                        <label>Widget <select v-model="newApplicationWidget" class="theme-add-select" @change="syncNewApplicationPart"><option v-for="widget in applicationWidgets" :key="widget" :value="widget">{{ widgetOptionLabel(widget) }}</option></select></label>
                        <label>Part <select v-model="newApplicationPart" class="theme-add-select"><option v-for="part in partsForWidget(newApplicationWidget)" :key="part" :value="part">{{ partOptionLabel(part) }}</option></select></label>
                        <button class="theme-add-button" @click="addApplication">+ Add assignment</button>
                    </div>
                    <div class="space-y-2">
                        <details v-for="application in applications" :key="application._line" class="theme-application-card theme-css-rule" :open="isDetailsOpen(`application:${application._line}`)" @toggle="setDetailsOpen(`application:${application._line}`, $event)">
                            <summary class="theme-application-summary">
                                <span class="theme-application-summary-title">{{ widgetLabel(application.w) }}</span>
                                <code>{{ application.w }}</code>
                                <span class="theme-application-summary-separator">·</span>
                                <span>{{ partLabel(application.p) }}</span>
                                <code>{{ application.p }}</code>
                                <span class="theme-application-summary-count">{{ application.x.length }} linked styles</span>
                            </summary>
                            <div class="theme-application-body">
                              <div class="theme-assignment-editor">
                                <div class="theme-css-selector">
                                <span class="theme-css-label">Widget</span>
                                <select :value="application.w" disabled><option :value="application.w">{{ widgetOptionLabel(application.w) }}</option></select>
                                <span class="theme-css-label">Part</span>
                                <select v-model="application.p" @change="markDirty"><option v-for="part in partsForWidget(application.w)" :key="part" :value="part">{{ partOptionLabel(part) }}</option></select>
                                </div>
                                <button class="theme-delete-button" title="Remove widget styling" @click="removeApplication(application)">Delete row</button>
                                <div class="theme-style-chain">
                                <div class="theme-runtime-order"><span>Firmware precedence (first wins):</span> <strong>{{ runtimeStyleOrder(application) || 'No linked styles' }}</strong></div>
                                <div v-for="(styleId, index) in application.x" :key="`${application._line}-${index}`" class="theme-style-ref">
                                    <div class="theme-style-ref-header">
                                        <select :value="styleId" @change="changeStyleReference(application, index, $event.target.value)"><option v-for="style in styles" :key="style.declaration.n" :value="style.declaration.n">{{ styleOptionLabel(style.declaration.n) }}</option></select>
                                        <button title="Move style earlier" :disabled="index === 0" @click="moveStyle(application, index, -1)">↑</button>
                                        <button title="Move style later" :disabled="index === application.x.length - 1" @click="moveStyle(application, index, 1)">↓</button>
                                        <button title="Remove style reference" @click="removeStyleReference(application, index)">×</button>
                                    </div>
                                    <div class="theme-style-preview" role="tooltip">
                                        <div class="theme-style-preview-title"><span>{{ styleLabel(styleId) }}</span><code>{{ styleId }}</code></div>
                                        <div v-for="group in stylePreviewGroups(styleId)" :key="group.state" class="theme-style-preview-state">
                                            <span class="theme-style-preview-state-label">{{ stateLabel(group.state) }}</span>
                                            <span v-for="property in group.properties" :key="property._line" class="theme-style-preview-property">
                                                {{ propertyLabel(property.k) }}: <span v-if="isColorProperty(property.k)" class="theme-color-bullet theme-preview-color-bullet" :style="{ backgroundColor: propertyColorHex(property) }"></span><strong>{{ stylePreviewValue(property) }}</strong>
                                            </span>
                                        </div>
                                        <div v-if="!stylePreviewGroups(styleId).length" class="theme-style-preview-empty">No properties in this style.</div>
                                        <div class="theme-style-preview-hint">Edit this style in the Styles tab.</div>
                                    </div>
                                </div>
                                <div class="theme-style-add">
                                    <select :value="''" @change="linkStyle(application, $event)"><option value="">+ Link style…</option><option v-for="style in styles" :key="style.declaration.n" :value="style.declaration.n">{{ styleOptionLabel(style.declaration.n) }}</option></select>
                                </div>
                                <div class="theme-cascade-inspector">
                                    <div class="theme-cascade-heading">
                                        <span>Effective theme values ({{ stateLabel(applicationState(application)) }})</span>
                                        <label class="theme-cascade-state">State
                                            <select :value="applicationState(application)" @change="setApplicationState(application, $event.target.value)">
                                                <option v-for="state in states" :key="state" :value="state">{{ stateOptionLabel(state) }}</option>
                                            </select>
                                        </label>
                                    </div>
                                    <div v-if="cascadeInspector(application).length" class="theme-cascade-list">
                                        <div v-for="entry in cascadeInspector(application)" :key="entry.property" class="theme-cascade-row">
                                            <span class="theme-cascade-property">{{ propertyLabel(entry.property) }}</span>
                                            <strong><span v-if="isColorProperty(entry.property)" class="theme-color-bullet theme-cascade-color-bullet" :style="{ backgroundColor: propertyColorHex(entry.declaration) }"></span>{{ propertyDisplayValue(entry.declaration) }}</strong>
                                            <span class="theme-cascade-source">{{ styleLabel(entry.sourceStyle) }} · {{ stateLabel(entry.sourceState) }} · {{ entry.matchLabel }}</span>
                                        </div>
                                    </div>
                                    <div v-else class="theme-cascade-empty">Link a style to inspect the resolved theme values.</div>
                                </div>
                                </div>
                            </div>
                              <aside class="theme-assignment-preview">
                                <div class="theme-assignment-preview-title">{{ widgetLabel(application.w) }} · {{ partLabel(application.p) }}</div>
                                <div class="theme-assignment-preview-state">{{ stateLabel(applicationState(application)) }} preview</div>
                                <div class="theme-assignment-preview-surface">
                                    <div v-if="isButtonWidget(application.w)" class="theme-preview-button" :style="applicationPreviewCss(application)">Button</div>
                                    <div v-else-if="application.w === 'bar'" class="theme-preview-bar" :style="applicationPreviewCss(application)"><span></span></div>
                                    <div v-else-if="application.w === 'slider'" class="theme-preview-slider" :style="applicationPreviewCss(application)"><span></span><i></i></div>
                                    <div v-else-if="application.w === 'sw'" class="theme-preview-switch" :style="applicationPreviewCss(application)"><i></i></div>
                                    <div v-else-if="application.w === 'cb'" class="theme-preview-checkbox" :style="applicationPreviewCss(application)">✓ Checked</div>
                                    <div v-else-if="application.w === 'chart'" class="theme-preview-chart" :style="applicationPreviewCss(application)"><i v-for="height in [35, 60, 45, 80, 55]" :key="height" :style="{ height: `${height}%` }"></i></div>
                                    <div v-else class="theme-preview-generic" :style="applicationPreviewCss(application)">{{ widgetPreviewText(application.w) }}</div>
                                </div>
                              </aside>
                            </div>
                        </details>
                    </div>
                </section>

                <section v-else-if="activeTab === 'preview'" class="theme-section">
                    <div class="theme-section-heading"><div><h2>Preview</h2><p>Live previews use the same Konva widget renderers as the designer. Preview styling is never exported.</p></div><span class="theme-chip">{{ previewState }}</span></div>
                    <div class="mb-4 flex flex-wrap gap-1"><button v-for="state in previewStates" :key="state" class="theme-state-button" :class="{ active: previewState === state }" @click="previewState = state">{{ state }}</button></div>
                    <div class="preview-board" :style="previewCss('scr', 'main')">
                        <div class="mb-3 text-xs opacity-70">Real designer widget renderers · screen / object</div>
                        <div class="preview-widget-grid">
                            <div v-for="widget in previewWidgets" :key="widget.type" class="preview-widget-card">
                                <div class="preview-widget-title"><span>{{ widget.label }}</span><code>{{ widget.type }}</code></div>
                                <ThemeWidgetPreview :widget="widget" :theme="previewTheme" :state="previewState" />
                            </div>
                        </div>
                    </div>
                </section>

                <section v-else class="theme-section">
                    <div class="theme-section-heading"><div><h2>Validation & raw records</h2><p>Use this view to inspect preserved records that are not exposed by the semantic editor.</p></div><span class="theme-chip" :class="validationMessage ? 'theme-chip-error' : 'theme-chip-ok'">{{ validationMessage ? 'invalid' : 'valid' }}</span></div>
                    <div v-if="validationMessage" class="mb-4 rounded border border-red-700 bg-red-950/60 p-3 text-red-200">{{ validationMessage }}</div>
                    <div v-else class="mb-4 rounded border border-emerald-700 bg-emerald-950/40 p-3 text-emerald-200">Theme is valid for the supported external LVGL 7 format.</div>
                    <details class="theme-card" :open="isDetailsOpen('advanced:unknown')" @toggle="setDetailsOpen('advanced:unknown', $event)"><summary class="cursor-pointer px-3 py-2 font-semibold">Preserved unknown records ({{ unknownRecords.length }})</summary><pre class="max-h-[28rem] overflow-auto border-t border-slate-700 p-3 text-[11px] text-slate-300">{{ rawUnknown }}</pre></details>
                    <details class="theme-card" :open="isDetailsOpen('advanced:serialized')" @toggle="setDetailsOpen('advanced:serialized', $event)"><summary class="cursor-pointer px-3 py-2 font-semibold">All serialized JSONL</summary><pre class="max-h-[28rem] overflow-auto border-t border-slate-700 p-3 text-[11px] text-slate-300">{{ serializedTheme }}</pre></details>
                </section>
            </main>
        </div>
    </div>
</template>

<script>
import defaultThemeText from '../../docs/themes/material-dark.jsonl?raw';
import { ColorPicker } from 'vue3-colorpicker';
import 'vue3-colorpicker/style.css';
import { objectTypelookup } from '../utils/lookupTables';
import ThemeWidgetPreview from './ThemeWidgetPreview.vue';
import {
    STATES, WIDGETS, PARTS, PROPERTIES, parseThemeJsonl,
    serializeThemeJsonl, validateThemeDocument, rgb565ToHex, hexToRgb565,
    propertyDescription, propertyRange, isColorProperty, rebuildIndexes,
} from './themeModel.js';
import { externalThemeToPreviewTheme, PREVIEW_THEME_FONT_FAMILY, previewThemeFontSizes } from './themePreview.js';
import { STYLE_CATALOG, THEME_PROPERTY_DEFINITIONS, themePropertyDefinition } from './themeV2Properties.js';

const PREVIEW_META = {
    page: { label: 'Page', themeWidget: 'scr', example: 'Screen' },
    obj: { label: 'Object', example: 'Object' },
    btn: { label: 'Button', example: 'Button' },
    label: { label: 'Label', example: 'Label text' },
    switch: { label: 'Switch', themeWidget: 'sw', kind: 'switch', example: 'On' },
    checkbox: { label: 'Checkbox', themeWidget: 'cb', kind: 'checkbox', example: 'Checked' },
    img: { label: 'Image', example: 'Image' },
    bar: { label: 'Bar', kind: 'bar', example: 'Bar' },
    slider: { label: 'Slider', kind: 'slider', example: 'Slider' },
    arc: { label: 'Arc', example: 'Arc' },
    linemeter: { label: 'Line meter', themeWidget: 'lmeter', example: 'Line meter' },
    tabview: { label: 'Tabview', example: 'Tabview' },
    tab: { label: 'Tab', themeWidget: 'tabview', part: 'tab_btn', example: 'Tab' },
    gauge: { label: 'Gauge', example: 'Gauge' },
    btnmatrix: { label: 'Button matrix', themeWidget: 'btnm', part: 'bg', example: 'A   B   C' },
    cpicker: { label: 'Color picker', example: 'Color picker' },
    dropdown: { label: 'Dropdown', themeWidget: 'ddlist', example: 'Option 1  ▾' },
    led: { label: 'LED', example: '●' },
    msgbox: { label: 'Messagebox', themeWidget: 'mbox', example: 'Message box' },
    qrcode: { label: 'QR code', example: '▦ QR code' },
    roller: { label: 'Roller', example: 'Option 1' },
    spinner: { label: 'Spinner', example: '◌ Loading' },
    line: { label: 'Line', example: '────────' },
    chart: { label: 'Chart', kind: 'chart', example: 'Chart' },
    legend: { label: 'Legend', example: 'Legend' },
    badge: { label: 'Badge', example: 'Badge' },
    connection: { label: 'Connection', example: 'Connection' },
    '3d': { label: '3D model', example: '3D model' },
};

// These are editor infrastructure or require a real external resource/peer
// object, so they do not have a meaningful standalone theme preview.
const PREVIEW_EXCLUDED_TYPES = new Set(['template_page', 'tab', 'connection', '3d', 'h3d']);

const THEME_WIDGET_PARTS = {
    scr: ['main'], obj: ['main'], cont: ['main'], btn: ['main'], btnm: ['bg', 'btn'], kb: ['bg', 'btn'],
    bar: ['bg', 'indic'], sw: ['bg', 'indic', 'knob'], canvas: ['main'], img: ['main'], imgbtn: ['main'],
    label: ['main'], line: ['main'], arc: ['bg', 'indic', 'knob'], spinner: ['bg', 'indic'], slider: ['bg', 'indic', 'knob'],
    cb: ['bg', 'bullet'], mbox: ['bg', 'btn_bg', 'btn'], led: ['main'], page: ['bg', 'scrollable', 'scrollbar', 'edge_flash'],
    tabview: ['bg', 'tab_bg', 'indic', 'tab_btn'], tabview_page: ['scrollable'], tileview: ['bg', 'scrollbar', 'edge_flash'],
    roller: ['bg', 'selected'], objmask: ['main'], list: ['bg', 'scrollbar'], list_btn: ['main'],
    ddlist: ['main', 'list', 'scrollbar', 'selected'], chart: ['bg', 'series_bg', 'cursor', 'series'],
    table: ['bg', 'cell1', 'cell2', 'cell3', 'cell4'], win: ['bg', 'scrollbar', 'content_scrollable', 'header'],
    win_btn: ['main'], ta: ['bg', 'placeholder', 'cursor', 'scrollbar'], spinbox: ['bg', 'cursor'], spinbox_btn: ['main'],
    cpicker: ['main', 'knob'], gauge: ['main', 'major', 'needle'], lmeter: ['main'],
    calendar: ['bg', 'date', 'header', 'day_names'],
};

// These are presentation labels only. The wire identifiers remain the fixed
// LVGL/openHASP identifiers from themeModel.js.
const WIDGET_LABELS = {
    scr: 'Screen', obj: 'Object', cont: 'Container', btn: 'Button', btnm: 'Button matrix', kb: 'Keyboard',
    bar: 'Bar', sw: 'Switch', canvas: 'Canvas', img: 'Image', imgbtn: 'Image button', label: 'Label',
    line: 'Line', arc: 'Arc', spinner: 'Spinner', slider: 'Slider', cb: 'Checkbox', mbox: 'Message box',
    led: 'LED', page: 'Page', tabview: 'Tab view', tabview_page: 'Tab view page', tileview: 'Tile view',
    roller: 'Roller', objmask: 'Object mask', list: 'List', list_btn: 'List button', ddlist: 'Dropdown list',
    chart: 'Chart', table: 'Table', win: 'Window', win_btn: 'Window button', ta: 'Text area', spinbox: 'Spinbox',
    spinbox_btn: 'Spinbox button', calendar: 'Calendar', cpicker: 'Color picker', lmeter: 'Line meter', gauge: 'Gauge',
};

const PART_LABELS = {
    main: 'Main content', bg: 'Background', indic: 'Indicator', knob: 'Knob', bullet: 'Bullet', btn: 'Button area',
    scrollable: 'Scrollable area', scrollbar: 'Scrollbar', edge_flash: 'Edge flash', selected: 'Selected item',
    list: 'List', tab_bg: 'Tab background', tab_btn: 'Tab button', series_bg: 'Series background', cursor: 'Cursor',
    series: 'Series', btn_bg: 'Button background', placeholder: 'Placeholder', content_scrollable: 'Scrollable content',
    header: 'Header', date: 'Date', day_names: 'Day names', major: 'Major scale', needle: 'Needle', cell1: 'Cell 1',
    cell2: 'Cell 2', cell3: 'Cell 3', cell4: 'Cell 4',
};

const STYLE_LABELS = {
    scr: 'Screen background', bg: 'Base background', bg_sec: 'Secondary background', bg_click: 'Pressed / checked background',
    btn: 'Button appearance', pad_inner: 'Inner padding', pad_small: 'Small padding', bar_bg: 'Bar track',
    bar_indic: 'Bar indicator', sw_knob: 'Switch knob', slider_knob: 'Slider knob', slider_bg: 'Slider track',
    arc_indic: 'Arc indicator', arc_bg: 'Arc background', arc_knob: 'Arc knob', led: 'LED', lmeter: 'Line meter',
    gauge_main: 'Gauge face', gauge_strong: 'Gauge strong marks', gauge_needle: 'Gauge needle', chart_bg: 'Chart background',
    chart_series_bg: 'Chart series background', chart_series: 'Chart series', calendar_header: 'Calendar header',
    calendar_daynames: 'Calendar day names', calendar_date_nums: 'Calendar date numbers', cpicker_bg: 'Color picker background',
    cpicker_indic: 'Color picker indicator', cb_bg: 'Checkbox background', cb_bullet: 'Checkbox bullet', kb_bg: 'Keyboard background',
    mbox_bg: 'Message box background', sb: 'Scrollbar', edge_flash: 'Edge flash', ta_cursor: 'Text area cursor',
    ta_placeholder: 'Text area placeholder', spinbox_cursor: 'Spinbox cursor', list_bg: 'List background', list_btn: 'List button',
    ddlist_page: 'Dropdown list page', ddlist_sel: 'Dropdown selected item', roller_bg: 'Roller background',
    roller_sel: 'Roller selected item', table_cell: 'Table cell', tabview_btns_bg: 'Tab view button background',
    tabview_btns: 'Tab view buttons', tabview_indic: 'Tab view indicator', tabview_page_scrl: 'Tab view scrollable page',
};

const STATE_META = {
    d: { label: 'Default', mask: 0 }, c: { label: 'Checked', mask: 0x01 }, f: { label: 'Focused', mask: 0x02 },
    e: { label: 'Edited', mask: 0x04 }, p: { label: 'Pressed', mask: 0x10 }, i: { label: 'Disabled', mask: 0x20 },
    pc: { label: 'Pressed + checked', mask: 0x11 }, fe: { label: 'Focused + edited', mask: 0x06 },
    ic: { label: 'Disabled + checked', mask: 0x21 }, fp: { label: 'Focused + pressed', mask: 0x12 },
};

const APPLICATION_WIDGETS = WIDGETS;
const MAX_THEME_COLORS = 64;

export default {
    name: 'ThemeEditor',
    components: { ColorPicker, ThemeWidgetPreview },
    props: {
        initialText: { type: String, default: '' },
        initialFileName: { type: String, default: 'theme.jsonl' },
    },
    data() {
        const sourceText = this.initialText || defaultThemeText;
        const document = parseThemeJsonl(sourceText);
        return {
            document,
            loadedText: sourceText,
            fileName: this.initialFileName,
            dirty: false,
            activeTab: 'preview',
            styleSearch: '',
            styleCatalogSearch: '',
            newStyleName: '',
            newColorName: '',
            newColorHex: '#ffffff',
            openColorMenu: null,
            detailsOpen: {},
            applicationStates: {},
            propertyDrafts: Object.fromEntries(document.records.filter((record) => record.type === 's').map((record) => [record.data.n, { key: '', state: 'd' }])),
            previewState: 'default',
            previewStates: ['default', 'pressed', 'checked', 'focused', 'edited', 'disabled'],
            newApplicationWidget: APPLICATION_WIDGETS[0] || 'scr',
            newApplicationPart: 'main',
            tabs: [],
        };
    },
    created() {
        this.updateTabs();
        this._beforeUnload = (event) => {
            if (!this.dirty) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', this._beforeUnload);
    },
    beforeUnmount() {
        window.removeEventListener('beforeunload', this._beforeUnload);
    },
    methods: {
        propertyDescription,
        propertyRange,
        isColorProperty,
        markDirty() { this.dirty = true; this.updateTabs(); },
        updateTabs() {
            this.tabs = [
                { id: 'metadata', label: 'Metadata' },
                { id: 'colors', label: 'Colors', count: this.colors?.length },
                { id: 'styles', label: 'Styles', count: this.styles?.length },
                { id: 'applications', label: 'Applications', count: this.applications?.length },
                { id: 'preview', label: 'Preview' },
                { id: 'advanced', label: 'Validation / raw', count: this.unknownRecords?.length || undefined },
            ];
        },
        saveTheme() {
            try { validateThemeDocument(this.document); } catch (error) { this.activeTab = 'advanced'; window.alert(`Theme is not valid: ${error.message}`); return; }
            const text = serializeThemeJsonl(this.document);
            this.loadedText = text;
            this.dirty = false;
            this.$emit('save', { text, fileName: this.serializedFileName });
        },
        colorHex(color) { return rgb565ToHex(color.v); },
        valueHex(value) { return rgb565ToHex(this.document.indexes.colors.get(value)?.v ?? 0); },
        colorReference(value) { return typeof value === 'string' && this.document.indexes.colors.has(value); },
        propertyDefinition(key) { return THEME_PROPERTY_DEFINITIONS[key] ?? themePropertyDefinition(key); },
        propertyLabel(key) { return this.propertyDefinition(key).label; },
        propertyEditorKind(property) {
            // LVGL accepts the special radius value "circle" in addition to
            // numeric radii. Keep that existing theme value editable instead
            // of forcing it through a number input.
            if (property.k === 'r') return typeof property.v === 'number' ? 'number' : 'text';
            const type = this.propertyDefinition(property.k).type;
            if (type === 'color') return 'color';
            if (type === 'enum') return 'enum';
            if (type === 'bool') return 'bool';
            if (type === 'int' || type === 'float' || typeof property.v === 'number') return 'number';
            return 'text';
        },
        radiusIsFullyRounded(property) { return property.k === 'r' && (property.v === 'circle' || Number(property.v) === 999); },
        toggleRadius(property, enabled) { this.setPropertyValue(property, enabled ? 999 : 5, true); },
        propertyColorHex(property) {
            if (this.colorReference(property.v)) return this.valueHex(property.v);
            return typeof property.v === 'number' ? rgb565ToHex(property.v) : '#000000';
        },
        selectedColorLabel(property) {
            const selected = this.colors.find((color) => color.n === property.v)
                || (typeof property.v === 'number' ? this.colors.find((color) => color.v === property.v) : null);
            return selected?.n ?? 'Choose predefined color';
        },
        toggleColorMenu(propertyLine) { this.openColorMenu = this.openColorMenu === propertyLine ? null : propertyLine; },
        closeColorMenu() { this.openColorMenu = null; },
        isDetailsOpen(key) { return this.detailsOpen[key] === true; },
        setDetailsOpen(key, event) { this.detailsOpen[key] = Boolean(event.target.open); },
        selectPropertyColor(property, colorName) {
            this.setPropertyValue(property, colorName);
            this.closeColorMenu();
        },
        defaultPropertyValue(key) {
            const definition = this.propertyDefinition(key);
            if (definition.type === 'color') return this.colors[0]?.n ?? 0;
            if (definition.type === 'enum') return definition.values?.[0] ?? '';
            if (definition.type === 'bool') return false;
            if (definition.type === 'string') return '';
            return definition.min ?? 0;
        },
        styleLabel(styleName) {
            if (!styleName) return 'Unknown style';
            return STYLE_LABELS[styleName] ?? styleName.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
        },
        styleDescription(styleName) { return STYLE_LABELS[styleName] ? `Firmware style for ${STYLE_LABELS[styleName].toLowerCase()}.` : 'Firmware-defined reusable style.'; },
        styleOptionLabel(styleName) { return `${this.styleLabel(styleName)} (${styleName})`; },
        widgetLabel(widget) { return WIDGET_LABELS[widget] ?? this.styleLabel(widget); },
        widgetOptionLabel(widget) { return `${this.widgetLabel(widget)} (${widget})`; },
        partLabel(part) { return PART_LABELS[part] ?? this.styleLabel(part); },
        partOptionLabel(part) { return `${this.partLabel(part)} (${part})`; },
        addColor() {
            const name = this.newColorName.trim();
            if (!name) return;
            if (this.colors.length >= MAX_THEME_COLORS) return;
            if (this.colors.some((color) => color.n === name)) {
                window.alert(`Color identifier "${name}" already exists.`);
                return;
            }
            let value;
            try { value = hexToRgb565(this.newColorHex); } catch { window.alert('Enter a valid RGB888 color such as #3fa7d1.'); return; }
            const record = { type: 'c', data: { r: 'c', n: name, v: value }, line: this.nextRecordLine() };
            const lastColor = this.document.records.reduce((index, item, itemIndex) => item.type === 'c' ? itemIndex : index, -1);
            const insertAt = lastColor >= 0 ? lastColor + 1 : this.document.records.findIndex((item) => item.type === 's');
            this.document.records.splice(insertAt < 0 ? this.document.records.length : insertAt, 0, record);
            this.newColorName = '';
            this.newColorHex = '#ffffff';
            rebuildIndexes(this.document);
            this.markDirty();
        },
        setColor565(color, value) {
            const next = Number(value);
            if (!Number.isInteger(next) || next < 0 || next > 65535) return;
            const record = this.document.records.find((item) => item.type === 'c' && (item.data === color || item.data.n === color.n || item.line === color._line));
            if (!record) return;
            record.data.v = next;
            rebuildIndexes(this.document);
            this.markDirty();
        },
        setColorHex(color, hex) { try { this.setColor565(color, hexToRgb565(hex)); } catch { /* keep the previous valid value */ } },
        renameColor(color, nextName) {
            if (!nextName || nextName === color.n) return;
            if (this.colors.some((item) => item.n === nextName)) { window.alert(`Color identifier "${nextName}" already exists.`); return; }
            const oldName = color.n;
            const record = this.document.records.find((item) => item.type === 'c' && item.data.n === oldName);
            if (record) record.data.n = nextName;
            for (const item of this.document.records) if (item.type === 'p' && item.data.v === oldName) item.data.v = nextName;
            rebuildIndexes(this.document); this.markDirty();
        },
        isKnownProperty(key) { return PROPERTIES.includes(key); },
        isEditableProperty(property) { return this.isKnownProperty(property.k) && (typeof property.v === 'number' || typeof property.v === 'string'); },
        propertyValueText(value) { return typeof value === 'string' ? value : JSON.stringify(value); },
        stateGroups(style) { return STATES.map((state) => ({ state, properties: style.properties.filter((property) => property.q === state) })).filter((group) => group.properties.length); },
        stateLabel(state) { return STATE_META[state]?.label ?? state; },
        stateOptionLabel(state) { return `${this.stateLabel(state)} (${state})`; },
        stateMask(state) { return STATE_META[state]?.mask ?? 0; },
        styleProperties(styleId, state = null) {
            const style = this.styles.find((item) => item.declaration.n === styleId);
            if (!style) return [];
            return state ? style.properties.filter((property) => property.q === state) : style.properties;
        },
        themeStyleEntries(widget, part) {
            const additions = [];
            for (const application of this.applications) {
                if (application.w !== widget || application.p !== part) continue;
                for (const styleId of application.x) {
                    const style = this.styles.find((item) => item.declaration.n === styleId);
                    if (style) additions.push({ style, styleId, application });
                }
            }
            // LVGL inserts every new theme style at the head of its list. The
            // runtime precedence is therefore the reverse of JSONL authoring
            // order (x order, then a-record order).
            return additions.reverse();
        },
        runtimeStyleOrder(application) {
            return this.themeStyleEntries(application.w, application.p).map((entry) => this.styleLabel(entry.styleId)).join(' → ');
        },
        applicationState(application) { return this.applicationStates[application._line] || 'd'; },
        setApplicationState(application, state) {
            if (STATES.includes(state)) this.applicationStates[application._line] = state;
        },
        resolveThemeProperty(widget, part, property, state) {
            const activeMask = this.stateMask(state);
            const entries = this.themeStyleEntries(widget, part);
            const candidates = entries.flatMap((entry, runtimeIndex) => entry.style.properties
                .filter((declaration) => declaration.k === property)
                .map((declaration) => ({ declaration, entry, styleId: entry.styleId, runtimeIndex, mask: this.stateMask(declaration.q) })));
            const exact = candidates.find((candidate) => candidate.declaration.q === state);
            if (exact) return { ...exact, matchLabel: 'Exact match' };

            let best;
            for (const candidate of candidates) {
                if ((candidate.mask & activeMask) !== candidate.mask) continue;
                if (!best || candidate.mask > best.mask) best = candidate;
            }
            return best ? { ...best, matchLabel: 'State fallback' } : null;
        },
        cascadeInspector(application) {
            const keys = [...new Set(this.themeStyleEntries(application.w, application.p)
                .flatMap((entry) => entry.style.properties.map((property) => property.k)))];
            const state = this.applicationState(application);
            return keys.map((property) => {
                const resolved = this.resolveThemeProperty(application.w, application.p, property, state);
                if (!resolved) return null;
                return {
                    property,
                    declaration: resolved.declaration,
                    sourceStyle: resolved.styleId,
                    sourceState: resolved.declaration.q,
                    matchLabel: resolved.matchLabel,
                };
            }).filter(Boolean);
        },
        stylePreviewGroups(styleId) {
            const properties = this.styleProperties(styleId);
            return STATES.map((state) => ({ state, properties: properties.filter((property) => property.q === state) }))
                .filter((group) => group.properties.length);
        },
        propertyDisplayValue(property) {
            if (property.k === 'r' && this.radiusIsFullyRounded(property)) return 'Fully rounded (999)';
            if (this.isColorProperty(property.k)) {
                const rgb565 = this.colorReference(property.v) ? this.document.indexes.colors.get(property.v)?.v : property.v;
                if (typeof rgb565 === 'number') {
                    const reference = this.colorReference(property.v) ? `${property.v} · ` : '';
                    return `${reference}${rgb565ToHex(rgb565)} · RGB565 ${rgb565}`;
                }
            }
            if (typeof property.v === 'boolean') return property.v ? 'on' : 'off';
            return String(property.v);
        },
        stylePreviewValue(property) {
            if (this.isColorProperty(property.k) && this.colorReference(property.v)) return property.v;
            return this.propertyDisplayValue(property);
        },
        updatePropertyRecord(property, value) {
            const record = this.document.records.find((item) => item.type === 'p' && (item.data === property || item.line === property._line));
            if (record) record.data.v = value;
            else property.v = value;
            this.markDirty();
        },
        setPropertyValue(property, rawValue, numeric = false) { this.updatePropertyRecord(property, numeric ? Number(rawValue) : rawValue); },
        setPropertyColor(property, rawColor) {
            try {
                const hex = typeof rawColor === 'string' ? rawColor : rawColor?.hex ?? rawColor?.toHex?.();
                const rgb565 = hexToRgb565(hex);
                if (this.colorReference(property.v)) {
                    // Theme color properties normally reference a named `c`
                    // record. The picker edits that shared color record, so
                    // every style and preview using it updates immediately.
                    const colorRecord = this.document.records.find((item) => item.type === 'c' && item.data.n === property.v);
                    if (colorRecord) colorRecord.data.v = rgb565;
                    rebuildIndexes(this.document);
                    this.markDirty();
                    return;
                }
                if (typeof property.v === 'number') {
                    this.updatePropertyRecord(property, rgb565);
                    return;
                }
                const nearest = this.nearestColor(rgb565);
                if (nearest) this.updatePropertyRecord(property, nearest);
            } catch { /* keep the previous valid value */ }
        },
        nearestColor(value) {
            let best = this.colors[0]?.n;
            let bestDistance = Infinity;
            for (const color of this.colors) { const distance = Math.abs(Number(color.v) - value); if (distance < bestDistance) { bestDistance = distance; best = color.n; } }
            return best || value;
        },
        changeStyleReference(application, index, value) { application.x[index] = value; this.markDirty(); },
        addStyleReference(application, styleId) {
            if (!styleId || application.x.includes(styleId) || application.x.length >= 4) return;
            application.x.push(styleId);
            this.markDirty();
        },
        linkStyle(application, event) {
            this.addStyleReference(application, event.target.value);
            event.target.value = '';
        },
        removeStyleReference(application, index) { application.x.splice(index, 1); this.markDirty(); },
        removeApplication(application) {
            const index = this.document.records.findIndex((record) => record.type === 'a' && record.data === application);
            if (index < 0) return;
            this.document.records.splice(index, 1);
            rebuildIndexes(this.document);
            this.markDirty();
        },
        availablePropertyKeys(style) {
            const draft = this.propertyDrafts[style.declaration.n] ?? { state: 'd' };
            return PROPERTIES.filter((key) => !style.properties.some((property) => property.k === key && property.q === draft.state));
        },
        insertAfterStyle(styleId, recordsToInsert) {
            const styleIndex = this.document.records.findIndex((record) => record.type === 's' && record.data.n === styleId);
            if (styleIndex < 0) return;
            let insertAt = styleIndex + 1;
            while (insertAt < this.document.records.length && this.document.records[insertAt].type === 'p' && this.document.records[insertAt].data.s === styleId) insertAt += 1;
            this.document.records.splice(insertAt, 0, ...recordsToInsert);
        },
        addStyle() {
            const name = this.newStyleName;
            if (!name || this.styles.some((style) => style.declaration.n === name)) return;
            const record = { type: 's', data: { r: 's', n: name }, line: this.nextRecordLine() };
            const insertAt = this.document.records.findIndex((item) => item.type === 'a');
            this.document.records.splice(insertAt < 0 ? this.document.records.length : insertAt, 0, record);
            this.propertyDrafts[name] = { key: '', state: 'd' };
            this.newStyleName = '';
            rebuildIndexes(this.document);
            this.markDirty();
        },
        removeStyle(styleId) {
            this.document.records = this.document.records.filter((record) => !(record.type === 's' && record.data.n === styleId) && !(record.type === 'p' && record.data.s === styleId));
            for (const application of this.applications) application.x = application.x.filter((reference) => reference !== styleId);
            delete this.propertyDrafts[styleId];
            rebuildIndexes(this.document);
            this.markDirty();
        },
        addProperty(styleId) {
            const draft = this.propertyDrafts[styleId];
            if (!draft?.key) return;
            const style = this.styles.find((item) => item.declaration.n === styleId);
            if (!style || style.properties.some((property) => property.k === draft.key && property.q === draft.state)) return;
            this.insertAfterStyle(styleId, [{ type: 'p', data: { r: 'p', s: styleId, q: draft.state, k: draft.key, v: this.defaultPropertyValue(draft.key) }, line: this.nextRecordLine() }]);
            draft.key = '';
            rebuildIndexes(this.document);
            this.markDirty();
        },
        removeProperty(property) {
            const index = this.document.records.findIndex((record) => record.type === 'p' && record.data === property);
            if (index < 0) return;
            this.document.records.splice(index, 1);
            rebuildIndexes(this.document);
            this.markDirty();
        },
        nextRecordLine() { return this.document.records.reduce((max, record) => Math.max(max, record.line ?? 0), 0) + 1; },
        partsForWidget(widget) { return THEME_WIDGET_PARTS[widget] ?? PARTS; },
        syncNewApplicationPart() { if (!this.partsForWidget(this.newApplicationWidget).includes(this.newApplicationPart)) this.newApplicationPart = this.partsForWidget(this.newApplicationWidget)[0] ?? 'main'; },
        addApplication() {
            const data = { r: 'a', w: this.newApplicationWidget, p: this.newApplicationPart, x: [] };
            const line = this.nextRecordLine();
            this.document.records.push({ type: 'a', data, line });
            Object.defineProperty(data, '_line', { value: line, enumerable: false, configurable: true });
            rebuildIndexes(this.document);
            this.markDirty();
        },
        moveStyle(application, index, direction) { const other = index + direction; if (other < 0 || other >= application.x.length) return; [application.x[index], application.x[other]] = [application.x[other], application.x[index]]; this.markDirty(); },
        applicationPreviewCss(application) { return this.previewCss(application.w, application.p, this.applicationState(application)); },
        isButtonWidget(widget) { return ['btn', 'btnm', 'list_btn', 'win_btn', 'spinbox_btn'].includes(widget); },
        widgetPreviewText(widget) { return ({ scr: 'Screen', obj: 'Object', cont: 'Container', label: 'Label text', img: 'Image', line: '────────', arc: 'Arc', spinner: 'Loading…', roller: 'Option 1', ddlist: 'Option 1 ▾', calendar: 'Calendar', cpicker: 'Color picker', lmeter: 'Line meter', gauge: 'Gauge', table: 'Table', ta: 'Text area', spinbox: '0', kb: 'Keyboard', mbox: 'Message box', page: 'Page', tabview: 'Tab view', tileview: 'Tile view', list: 'List', led: '●', canvas: 'Canvas' })[widget] ?? this.widgetLabel(widget); },
        previewCss(widget, part, state = this.previewStateCode) {
            const css = {};
            // Keep padding and other layout geometry owned by the preview
            // widgets, but apply radius because it is directly visible in the
            // theme preview (999 means fully rounded in openHASP).
            const map = { bc: 'backgroundColor', tc: 'color', vc: 'color', bco: 'borderColor', bw: 'borderWidth', r: 'borderRadius', oc: 'outlineColor', ow: 'outlineWidth', shc: 'boxShadowColor' };
            for (const [property, cssKey] of Object.entries(map)) {
                const resolved = this.resolveThemeProperty(widget, part, property, state);
                if (!resolved) continue;
                const value = this.previewValue(resolved.declaration.v, property);
                if (value === undefined) continue;
                if (property === 'r') css[cssKey] = value === 'circle' ? '999px' : `${Math.max(0, Math.min(Number(value) || 0, 999))}px`;
                else css[cssKey] = typeof value === 'number' && ['bw', 'ow'].includes(property) ? `${Math.max(0, Math.min(value, 32))}px` : value;
            }
            const textFont = this.resolveThemeProperty(widget, part, 'tf', state);
            if (textFont) {
                const fontName = typeof textFont.declaration.v === 'string' ? textFont.declaration.v : undefined;
                const fontSize = previewThemeFontSizes(this.metadata?.w)[fontName];
                if (fontSize) {
                    css.fontSize = `${fontSize}px`;
                    css.fontFamily = PREVIEW_THEME_FONT_FAMILY;
                }
            }
            if (css.borderWidth && !css.borderStyle) css.borderStyle = 'solid';
            return css;
        },
        previewTrackStyle(widget, part) {
            return {
                position: 'relative',
                overflow: 'hidden',
                display: 'block',
                width: '100%',
                height: '24px',
                minHeight: '24px',
                ...this.previewCss(widget, part),
            };
        },
        previewFillStyle(widget, part) {
            return {
                position: 'absolute',
                left: '0',
                top: '0',
                bottom: '0',
                width: '64%',
                height: '100%',
                display: 'block',
                ...this.previewCss(widget, part),
            };
        },
        previewKnobStyle() {
            return { position: 'absolute', left: '60%', top: '50%', width: '24px', height: '24px', transform: 'translate(-50%, -50%)', borderRadius: '999px' };
        },
        previewValue(value, property) {
            // mono and hasp-dark store many theme colours as literal RGB565
            // integers, while material-dark mostly uses named colour records.
            // A number is otherwise invalid as a CSS colour and silently
            // leaves the preview looking like its hardcoded fallback.
            if (isColorProperty(property)) {
                if (typeof value === 'string' && this.document.indexes.colors.has(value)) return rgb565ToHex(this.document.indexes.colors.get(value).v);
                if (typeof value === 'number') return rgb565ToHex(value);
            }
            if (typeof value === 'string' && this.document.indexes.colors.has(value)) return rgb565ToHex(this.document.indexes.colors.get(value).v);
            if (typeof value === 'number') return value;
            return value;
        },
    },
    computed: {
        metadata() { return this.document.records.find((record) => record.type === 'm')?.data; },
        colors() { return this.document.records.filter((record) => record.type === 'c').map((record) => ({ ...record.data, _line: record.line })); },
        styles() {
            return this.document.records.filter((record) => record.type === 's').map((record) => ({
                declaration: record.data,
                properties: this.document.records
                    .filter((item) => item.type === 'p' && item.data.s === record.data.n)
                    .map((item) => {
                        // Non-enumerable UI metadata keeps edits on the actual
                        // source object without leaking _line into JSONL.
                        Object.defineProperty(item.data, '_line', { value: item.line, enumerable: false, configurable: true });
                        return item.data;
                    }),
            }));
        },
        applications() {
            return this.document.records.filter((record) => record.type === 'a').map((record) => {
                Object.defineProperty(record.data, '_line', { value: record.line, enumerable: false, configurable: true });
                return record.data;
            });
        },
        unknownRecords() { return this.document.records.filter((record) => record.type === 'unknown'); },
        rawUnknown() { return this.unknownRecords.map((record) => JSON.stringify(record.data)).join('\n') || 'None'; },
        serializedTheme() { try { return serializeThemeJsonl(this.document); } catch { return ''; } },
        validationMessage() { try { validateThemeDocument(this.document); return ''; } catch (error) { return error.message; } },
        filteredStyles() { const needle = this.styleSearch.trim().toLowerCase(); if (!needle) return this.styles; return this.styles.filter((style) => style.declaration.n.toLowerCase().includes(needle) || style.properties.some((property) => `${property.k} ${property.v}`.toLowerCase().includes(needle))); },
        firstStyleId() { return this.filteredStyles[0]?.declaration.n; },
        widgets() { return WIDGETS; },
        parts() { return PARTS; },
        states() { return STATES; },
        applicationWidgets() { return APPLICATION_WIDGETS; },
        previewTheme() { try { return externalThemeToPreviewTheme(this.serializedTheme); } catch { return {}; } },
        maxThemeColors() { return MAX_THEME_COLORS; },
        availableStyleNames() { return STYLE_CATALOG.filter((name) => !this.styles.some((style) => style.declaration.n === name)); },
        filteredAvailableStyleNames() {
            const needle = this.styleCatalogSearch.toLowerCase();
            if (!needle) return this.availableStyleNames;
            return this.availableStyleNames.filter((name) => `${name} ${this.styleLabel(name)}`.toLowerCase().includes(needle));
        },
        previewWidgets() {
            return Object.keys(objectTypelookup)
                .filter((type) => !PREVIEW_EXCLUDED_TYPES.has(type))
                .map((type) => ({
                    type,
                    themeWidget: type,
                    part: 'main',
                    kind: 'generic',
                    label: type,
                    example: type,
                    ...PREVIEW_META[type],
                }));
        },
        serializedFileName() { return `${(this.metadata?.n || this.fileName || 'theme').replace(/\.jsonl$/i, '')}.jsonl`; },
        previewStateCode() { return ({ default: 'd', pressed: 'p', checked: 'c', focused: 'f', edited: 'e', disabled: 'i' })[this.previewState] || 'd'; },
    },
};
</script>

<style scoped>
.theme-editor { background: #1e293b; }
.theme-button, .theme-mini-button, .theme-state-button { border: 1px solid #475569; background: #334155; border-radius: 3px; padding: 4px 8px; font-size: 11px; color: #e2e8f0; }
.theme-button:hover, .theme-mini-button:hover, .theme-state-button:hover, .theme-state-button.active { background: #0e7490; border-color: #22d3ee; }
.theme-button-close { background: #7f1d1d; border-color: #991b1b; }
.theme-nav-button { width: 100%; border-radius: 3px; padding: 7px 8px; text-align: left; color: #cbd5e1; }
.theme-nav-button:hover, .theme-nav-button.active { background: #164e63; color: #fff; }
.theme-section { max-width: 1100px; margin: 0 auto; }
.theme-section-heading { display: flex; align-items: start; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
.theme-section h2 { font-size: 18px; font-weight: 600; color: #f8fafc; }
.theme-section p { margin-top: 3px; font-size: 11px; color: #94a3b8; }
.theme-chip { flex-shrink: 0; border-radius: 999px; background: #334155; padding: 4px 9px; font-size: 10px; color: #cbd5e1; }
.theme-chip-error { background: #7f1d1d; color: #fecaca; }
.theme-chip-ok { background: #14532d; color: #bbf7d0; }
.theme-field { display: flex; align-items: center; gap: 8px; border: 1px solid #475569; border-radius: 4px; background: #1e293b; padding: 8px; font-size: 11px; }
.theme-field span { width: 42%; color: #cbd5e1; }
.theme-field small { color: #64748b; }
.theme-field input, .theme-table input, .theme-property-row input, .theme-property-row select, .theme-application-card select, .theme-style-ref select { min-width: 0; flex: 1; border: 1px solid #64748b; border-radius: 3px; background: #f8fafc; padding: 3px 5px; color: #0f172a; font-size: 11px; }
.theme-check-field input { width: 16px; flex: none; }
.theme-table { overflow: hidden; border: 1px solid #475569; border-radius: 4px; }
.theme-table-row { display: grid; grid-template-columns: 64px minmax(130px, 1fr) minmax(120px, 1fr) minmax(100px, 1fr); align-items: center; gap: 8px; border-top: 1px solid #334155; padding: 5px 8px; }
.theme-table-row:first-child { border-top: 0; }
.theme-table-header { background: #334155; color: #cbd5e1; font-size: 10px; }
.theme-color-picker { height: 24px; width: 40px; padding: 1px; }
.theme-card { overflow: hidden; border: 1px solid #475569; border-radius: 4px; background: #1e293b; }
.theme-state-group { border: 1px solid #334155; border-radius: 3px; padding: 7px; }
.theme-property-row { display: grid; grid-template-columns: minmax(200px, 1fr) minmax(180px, 1fr); align-items: center; gap: 10px; border-top: 1px solid #273449; padding: 4px 0; font-size: 11px; }
.theme-property-row:first-of-type { border-top: 0; }
.theme-radius-editor { display: flex; min-width: 0; align-items: center; gap: 8px; }
.theme-radius-editor > input { width: 82px; flex: 0 0 82px; }
.theme-radius-toggle { display: flex; align-items: center; gap: 4px; white-space: nowrap; color: #cbd5e1; font-size: 10px; }
.theme-radius-toggle input { width: 14px; height: 14px; accent-color: #0891b2; }
.theme-radius-editor > input:disabled { cursor: not-allowed; background: #cbd5e1; color: #475569; opacity: 1; }
.theme-color-menu-wrap { position: relative; min-width: 190px; }
.theme-style-card { overflow: visible; }
.theme-color-select { display: flex; width: 100%; align-items: center; gap: 6px; border: 1px solid #64748b; border-radius: 3px; background: #334155; padding: 3px 5px; color: #e2e8f0; font-size: 11px; text-align: left; }
.theme-color-select:hover { border-color: #0e7490; }
.theme-color-chevron { margin-left: auto; color: #475569; }
.theme-color-bullet { display: inline-block; width: 12px; height: 12px; flex: none; border: 1px solid #334155; border-radius: 50%; box-shadow: inset 0 0 0 1px rgb(255 255 255 / 25%); }
.theme-color-menu { position: absolute; z-index: 50; top: calc(100% + 3px); left: 0; width: 100%; max-height: min(220px, 40vh); overflow-y: auto; border: 1px solid #0e7490; border-radius: 3px; background: #0f172a; padding: 3px; box-shadow: 0 8px 20px rgb(0 0 0 / 45%); }
.theme-color-option { display: flex; width: 100%; align-items: center; gap: 6px; border-radius: 2px; padding: 4px 5px; color: #e2e8f0; font-size: 11px; text-align: left; }
.theme-color-option:hover { background: #164e63; color: #fff; }
.theme-color-option code { margin-left: auto; color: #94a3b8; font-size: 9px; }
.theme-search { width: 100%; margin-bottom: 10px; border: 1px solid #64748b; border-radius: 3px; background: #f8fafc; padding: 5px 7px; color: #0f172a; font-size: 11px; }
.theme-editor-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 7px; margin-bottom: 10px; border: 1px solid #475569; border-radius: 5px; background: #1e293b; padding: 8px; }
.theme-editor-toolbar label { display: flex; align-items: center; gap: 5px; color: #cbd5e1; font-size: 11px; }
.theme-color-add { align-items: center; }
.theme-text-input { min-width: 150px; border: 1px solid #64748b; border-radius: 3px; background: #f8fafc; padding: 4px 6px; color: #0f172a; font-size: 11px; }
.theme-hex-input { min-width: 92px; width: 92px; }
.theme-limit-warning { color: #fbbf24; font-size: 10px; }
.theme-style-search { min-width: 220px; border: 1px solid #64748b; border-radius: 3px; background: #f8fafc; padding: 4px 7px; color: #0f172a; font-size: 11px; }
.theme-add-select, .theme-add-property select, .theme-style-add select { min-width: 180px; border: 1px solid #64748b; border-radius: 3px; background: #f8fafc; padding: 4px 6px; color: #0f172a; font-size: 11px; }
.theme-add-button { border: 1px solid #0e7490; border-radius: 3px; background: #155e75; padding: 4px 8px; color: #cffafe; font-size: 11px; }
.theme-add-button:hover { background: #0891b2; color: #fff; }
.theme-add-button:disabled { cursor: not-allowed; opacity: .4; }
.theme-style-summary { display: flex; align-items: center; gap: 8px; padding: 8px 10px; cursor: pointer; }
.theme-style-name { color: #f8fafc; font-weight: 600; }
.theme-style-summary > code { color: #67e8f9; font-size: 10px; }
.theme-style-summary .theme-delete-button { margin-left: auto; }
.theme-style-description { color: #94a3b8; font-size: 10px; }
.theme-delete-small { padding: 3px 6px; }
.theme-property-label { color: #e2e8f0; }
.theme-property-delete { flex: none; width: 22px; height: 22px; border-radius: 3px; color: #fca5a5; font-size: 16px; line-height: 18px; }
.theme-property-delete:hover { background: #7f1d1d; color: #fff; }
.theme-add-property { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; border-top: 1px dashed #475569; padding-top: 8px; }
.theme-add-property select { min-width: 190px; }
.theme-empty-state { border: 1px dashed #475569; border-radius: 3px; padding: 10px; color: #94a3b8; font-size: 11px; }
.theme-application-card { display: block; border: 1px solid #475569; border-radius: 4px; background: #1e293b; padding: 0; }
.theme-application-card > select { flex: 0 0 120px; }
.theme-application-summary { display: flex; min-height: 34px; align-items: center; gap: 7px; padding: 7px 9px; cursor: pointer; color: #cbd5e1; font-size: 11px; }
.theme-application-summary:hover { background: #26364d; }
.theme-application-summary-title { color: #f8fafc; font-weight: 600; }
.theme-application-summary code { color: #67e8f9; font-size: 10px; }
.theme-application-summary-separator { color: #64748b; }
.theme-application-summary-count { margin-left: auto; color: #94a3b8; font-size: 10px; }
.theme-application-body { display: grid; grid-template-columns: minmax(0, 1fr) minmax(190px, 260px); align-items: stretch; gap: 10px; border-top: 1px solid #334155; padding: 7px; }
.theme-assignment-editor { display: flex; min-width: 0; flex-wrap: wrap; align-items: flex-start; gap: 6px; }
.theme-assignment-preview { display: flex; min-height: 100%; min-width: 0; flex-direction: column; justify-content: center; gap: 5px; border-left: 1px solid #475569; padding: 6px 4px 6px 12px; }
.theme-assignment-preview-title { color: #a5f3fc; font-size: 11px; font-weight: 600; }
.theme-assignment-preview-state { color: #94a3b8; font-size: 10px; }
.theme-assignment-preview-surface { display: flex; min-height: 94px; align-items: center; justify-content: center; overflow: hidden; border: 1px solid #475569; border-radius: 5px; background: #0f172a; padding: 12px; color: #e2e8f0; }
.theme-preview-button { min-width: 100px; border: 1px solid rgb(255 255 255 / 20%); border-radius: 8px; padding: 9px 16px; text-align: center; box-shadow: 0 2px 5px rgb(0 0 0 / 25%); }
.theme-preview-bar, .theme-preview-slider { position: relative; width: 85%; height: 18px; overflow: hidden; border-radius: 9px; background: rgb(255 255 255 / 15%); }
.theme-preview-bar span, .theme-preview-slider span { display: block; width: 62%; height: 100%; background: rgb(255 255 255 / 55%); }
.theme-preview-slider i { position: absolute; top: 50%; left: 62%; width: 22px; height: 22px; transform: translate(-50%, -50%); border-radius: 50%; background: #e2e8f0; }
.theme-preview-switch { position: relative; width: 64px; height: 30px; border-radius: 15px; background: rgb(255 255 255 / 20%); }
.theme-preview-switch i { position: absolute; top: 4px; right: 4px; width: 22px; height: 22px; border-radius: 50%; background: #e2e8f0; }
.theme-preview-checkbox { border: 1px solid rgb(255 255 255 / 20%); border-radius: 4px; padding: 8px 12px; }
.theme-preview-chart { display: flex; width: 85%; height: 62px; align-items: end; justify-content: space-evenly; gap: 4px; }
.theme-preview-chart i { display: block; width: 12%; min-height: 8px; background: currentColor; }
.theme-preview-generic { max-width: 100%; overflow: hidden; text-align: center; text-overflow: ellipsis; white-space: nowrap; }
.theme-style-ref { display: flex; align-items: center; gap: 2px; border-radius: 3px; background: #334155; padding: 2px; }
.theme-style-ref button { width: 19px; color: #cbd5e1; }
.theme-style-ref button:hover { color: #22d3ee; }
.theme-style-ref button:disabled { opacity: .3; }
.theme-css-rule { align-items: flex-start; }
.theme-css-selector { display: grid; grid-template-columns: auto minmax(90px, 1fr) auto minmax(90px, 1fr) auto minmax(150px, 1.4fr); align-items: center; gap: 5px; flex: 0 1 auto; min-width: min(100%, 600px); }
.theme-css-label { color: #94a3b8; font-size: 10px; }
.theme-css-selector select { min-width: 0; border: 1px solid #64748b; border-radius: 3px; background: #f8fafc; padding: 3px 5px; color: #0f172a; font-size: 11px; }
.theme-css-selector select:disabled { cursor: not-allowed; background: #cbd5e1; color: #475569; opacity: 1; }
.theme-delete-button { flex: none; border: 1px solid #7f1d1d; border-radius: 3px; background: #450a0a; padding: 4px 7px; color: #fecaca; font-size: 10px; }
.theme-delete-button:hover { background: #991b1b; color: #fff; }
.theme-style-chain { display: flex; min-width: 100%; flex: 1 1 100%; flex-direction: column; gap: 6px; border-top: 1px solid #334155; padding-top: 7px; }
.theme-runtime-order { border: 1px dashed #475569; border-radius: 3px; padding: 5px 7px; color: #94a3b8; font-size: 10px; }
.theme-runtime-order strong { color: #a5f3fc; font-weight: 500; }
.theme-style-ref { position: relative; display: block; }
.theme-style-ref-header { display: flex; align-items: center; gap: 2px; }
.theme-style-ref-header select { min-width: 140px; flex: 1; border: 1px solid #64748b; border-radius: 3px; background: #f8fafc; padding: 3px 5px; color: #0f172a; font-size: 11px; }
.theme-style-preview { display: none; position: absolute; z-index: 40; top: calc(100% + 5px); left: 0; width: min(520px, calc(100vw - 48px)); border: 1px solid #0e7490; border-radius: 4px; background: #0f172a; padding: 8px; box-shadow: 0 8px 24px rgb(0 0 0 / 35%); color: #cbd5e1; font-size: 10px; }
.theme-style-ref:hover .theme-style-preview, .theme-style-ref:focus-within .theme-style-preview { display: block; }
.theme-style-preview-title { display: flex; align-items: center; gap: 7px; border-bottom: 1px solid #334155; padding-bottom: 5px; color: #f8fafc; font-weight: 600; }
.theme-style-preview-title code { color: #67e8f9; font-weight: 400; }
.theme-style-preview-state { display: flex; flex-wrap: wrap; gap: 4px 8px; border-bottom: 1px solid #1e293b; padding: 5px 0; }
.theme-style-preview-state-label { min-width: 88px; color: #a5f3fc; font-weight: 600; }
.theme-style-preview-property strong { color: #f8fafc; font-weight: 500; }
.theme-preview-color-bullet { margin: 0 4px 0 2px; vertical-align: -2px; }
.theme-cascade-color-bullet { margin-right: 5px; vertical-align: -2px; }
.theme-style-preview-empty { padding: 7px 0; color: #94a3b8; }
.theme-style-preview-hint { padding-top: 6px; color: #64748b; font-style: italic; }
.theme-cascade-inspector { border-top: 1px solid #334155; padding-top: 7px; }
.theme-cascade-heading { display: flex; justify-content: space-between; gap: 8px; padding-bottom: 5px; color: #a5f3fc; font-size: 10px; font-weight: 600; }
.theme-cascade-state { display: flex; align-items: center; gap: 4px; color: #94a3b8; font-weight: 400; }
.theme-cascade-state select { min-width: 150px; border: 1px solid #64748b; border-radius: 3px; background: #f8fafc; padding: 3px 5px; color: #0f172a; font-size: 10px; }
.theme-cascade-list { display: grid; gap: 3px; }
.theme-cascade-row { display: grid; grid-template-columns: minmax(120px, 1fr) minmax(100px, 1fr) minmax(180px, 1.5fr); align-items: center; gap: 7px; border-left: 2px solid #0e7490; background: #172033; padding: 4px 6px; color: #e2e8f0; font-size: 10px; }
.theme-cascade-property { color: #cbd5e1; }
.theme-cascade-source { color: #94a3b8; text-align: right; }
.theme-cascade-empty { padding: 5px 0; color: #64748b; font-size: 10px; }
.theme-style-add { padding-top: 2px; }
@media (max-width: 850px) {
    .theme-application-body { grid-template-columns: 1fr; }
    .theme-assignment-preview { border-top: 1px solid #475569; border-left: 0; padding: 9px 0 2px; }
}
.preview-board { min-height: 480px; max-width: 1100px; border: 1px solid #475569; border-radius: 8px; padding: 18px; background: #0f172a; }
.preview-widget-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 10px; }
.preview-widget-card { min-width: 0; overflow: hidden; border: 1px solid #334155; border-radius: 5px; background: #1e293b; padding: 7px; }
.preview-widget-title { display: flex; align-items: baseline; justify-content: space-between; gap: 6px; margin-bottom: 6px; color: #cbd5e1; font-size: 11px; font-weight: 600; }
.preview-widget-title code { color: #64748b; font-size: 9px; font-weight: 400; }
.preview-sample { min-width: 0; min-height: 48px; max-height: 90px; border: 1px solid #475569; border-radius: 4px; padding: 8px; }
.preview-inline { display: flex; align-items: center; gap: 8px; }
.preview-generic { display: flex; align-items: center; justify-content: center; overflow: hidden; color: #cbd5e1; text-align: center; }
.preview-widget { min-width: 0; max-width: 100%; max-height: 140px; min-height: 58px; display: flex; align-items: center; gap: 8px; border: 1px solid #475569; border-radius: 5px; padding: 10px; }
.preview-bar, .preview-slider { position: relative; overflow: hidden; min-height: 22px; border-radius: 999px; padding: 0; }
.preview-bar-fill, .preview-slider-fill { position: absolute; inset: 0 auto 0 0; width: 64%; border-radius: inherit; }
.preview-slider-fill { width: 64%; }
.preview-slider-knob { position: absolute; left: 60%; top: 50%; width: 24px; height: 24px; transform: translate(-50%, -50%); border-radius: 999px; }
.preview-checkbox { display: inline-flex; height: 20px; width: 20px; align-items: center; justify-content: center; border: 1px solid #64748b; border-radius: 3px; }
.preview-switch { display: inline-block; height: 18px; width: 32px; border-radius: 999px; background: #64748b; }
.preview-chart { align-items: end; gap: 3px; padding: 12px; }
.preview-chart span { display: block; min-width: 9px; flex: 1; border-radius: 2px 2px 0 0; background: #38bdf8; }
code { color: #67e8f9; }
pre { white-space: pre-wrap; word-break: break-word; }
/* Keep every native control in the same dark theme as the editor surface. */
.theme-editor { color-scheme: dark; }
.theme-editor input:not([type='checkbox']):not([type='radio']):not([type='color']),
.theme-editor select,
.theme-editor textarea {
    background: #0f172a;
    border-color: #475569;
    color: #e2e8f0;
    color-scheme: dark;
}
.theme-editor input::placeholder,
.theme-editor textarea::placeholder { color: #64748b; }
.theme-editor input:focus,
.theme-editor select:focus,
.theme-editor textarea:focus { border-color: #22d3ee; outline: 1px solid rgb(34 211 238 / 25%); }
.theme-editor input:disabled,
.theme-editor select:disabled,
.theme-editor textarea:disabled { background: #1e293b; color: #94a3b8; border-color: #334155; opacity: 1; }
.theme-editor option { background: #0f172a; color: #e2e8f0; }
.theme-editor input[type='checkbox'],
.theme-editor input[type='radio'] { accent-color: #22d3ee; color-scheme: dark; }
@media (max-width: 800px) { .theme-property-row { grid-template-columns: 1fr; gap: 3px; } .theme-table-row { grid-template-columns: 40px 1fr 1fr; } .theme-table-row > :last-child { grid-column: 2 / -1; } .theme-button { padding: 3px 5px; } }
</style>
